<template>
  <div v-if="gamForm" :class="['location-form', { 'is-invalid': !isValid }]">
    <div class="name-address section">
      <div class="section-title">
        <span>Name &amp; address</span>
        <span class="required">・ Required</span>
      </div>

      <LocationCreateFields
        :search-list-id="GamListId.LOCATION_FORM"
        :dropdownlist-id="listId"
        :name-input="getNameInput"
        :set-form-value="setFormValue"
        :form-model="gamForm.form"
      />
    </div>

    <div class="location-types section">
      <div class="section-title">
        {{ localize('locations.add.form.section.type.title') }}
        <span class="required">・ Required</span>
      </div>

      <GamMapFiltersList
        v-bind="getLocTypeList"
        v-model="gamForm.form.type"
        :value="gamForm.form.type"
        :is-multi-select="false"
      />
    </div>

    <div class="chess-features section">
      <div class="section-title">
        {{ localize('locations.add.form.section.features.title') }}
      </div>

      <GamChessFeaturesList
        v-bind="getLocFeatureList"
        v-model="gamForm.form.chessFeatures"
        :value="gamForm.form.chessFeatures"
        :is-multi-select="true"
      />

      <gam-counter
        v-if="isChessSetSelected && gamForm.form.chessSetCount !== 0"
        v-bind="getChessSetCounter"
        v-model="gamForm.form.chessSetCount"
      />

      <GamSome v-else-if="isChessSetSelected" v-bind="getChessSetCounter" v-model="gamForm.form.chessSetCount">
        Some
      </GamSome>

      <gam-counter
        v-if="isChessClockSelected && gamForm.form.chessClockCount !== 0"
        v-bind="getChessClockCounter"
        v-model="gamForm.form.chessClockCount"
      />

      <GamSome v-else-if="isChessClockSelected" v-bind="getChessClockCounter" v-model="gamForm.form.chessClockCount">
        Some
      </GamSome>

      <gam-counter
        v-if="isChessTableSelected && gamForm.form.chessTableCount !== 0"
        v-bind="getChessTableCounter"
        v-model="gamForm.form.chessTableCount"
      />

      <GamSome v-else-if="isChessTableSelected" v-bind="getChessTableCounter" v-model="gamForm.form.chessTableCount">
        Some
      </GamSome>

      <gam-counter
        v-if="isGiantChessSelected && gamForm.form.giantChessBoardCount !== 0"
        v-bind="getGiantChessCounter"
        v-model="gamForm.form.giantChessBoardCount"
      />

      <GamSome
        v-else-if="isGiantChessSelected"
        v-bind="getGiantChessCounter"
        v-model="gamForm.form.giantChessBoardCount"
      >
        Some
      </GamSome>

      <GamSwitch v-if="isGiantChessSelected" v-bind="getSwitch()" v-model:checked="gamForm.form.giantChessPieces" />
    </div>

    <!--
    <div class="chess-features section">
      <div class="section-title">
        {{ localize('locations.add.form.section.vibes.title') }}
      </div>

      <div class="general-features">
        <GamChip
          v-for="(item, index) in locationVibeOptions"
          :key="`vibe-item-${index}`"
          ref="vibeItem"
          :label="item.label"
          :is-active="gamForm.form.generalFeatures?.includes(item.value)"
          @click="() => toggleGeneralFeature(item)"
        />
      </div>
    </div>
    -->

    <div class="additional-info section">
      <!--
      <gam-textarea
        v-model="gamForm.form.about"
        placeholder="locations.add.form.section.type.about"
        :value="gamForm.form.about"
      />
      -->

      <gam-scroll-items v-bind="getGamScrollItemType">
        <gam-image :is-multi-add="true" :size="GamImageSize.MEDIUM" @add="addImage" />
        <gam-image
          v-for="(image, index) in gamForm.form.photos"
          :key="`loc-img-${index}`"
          v-model="gamForm.form.photos[index]"
          :image="gamForm.form.photos[index]"
          :size="GamImageSize.MEDIUM"
          :no-filter="true"
          @expand="expandImage"
        />
      </gam-scroll-items>

      <GamSwitch
        :type="GamSwitchValueType.SWITCH"
        :position="GamSwitchPosition.RIGHT"
        :label="'locations.form.openHours.toggle'"
        name="open-hours-toggle"
        :checked="showOpenHours"
        @update:checked="handleToggleOpenHours"
      />

      <OpenHoursFields
        v-if="showOpenHours && gamForm?.form.openingHours"
        :model-value="gamForm.form.openingHours"
        @update:model-value="
          (v) => {
            if (gamForm) gamForm.form.openingHours = v;
          }
        "
      />
    </div>

    <gam-expand-image
      v-if="selectedImage"
      ref="imageExpand"
      v-bind="selectedImage"
      @close="clearSelected"
      @clear="deleteImage"
    />

    <div v-if="userDetails?.isAdmin && props.location" class="section">
      <GamSwitch
        v-model:checked="gamForm.form.isVetted"
        :type="GamSwitchValueType.CHECKBOX"
        :position="GamSwitchPosition.LEFT"
        label="Is vetted location"
        name="is-vetted"
      />
      <GamSwitch
        v-model:checked="gamForm.form.isSparked"
        :type="GamSwitchValueType.CHECKBOX"
        :position="GamSwitchPosition.LEFT"
        label="Is sparked location"
        name="is-sparked"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import type { AdminDetailLocationDto, GeoCoords, SearchLocationDto } from '@/core/data/location/location.interface';
import { LocationChessFeature, LocationType } from '@/core/data/location/location.type';
import { BlobSource } from '@/core/data/report/report.type';
import { gambits, localize, localizeWithValues } from '@/core/gambits';
import GamHelper from '@/core/helpers/app.helper';
import { useAuthStore } from '@/stores/auth.store';
import { useFormStore } from '@/stores/form.store';
import GamChessFeaturesList from '@/views/components/gam-map/GamChessFeaturesList.vue';
import GamMapFiltersList from '@/views/components/gam-map/GamMapFiltersList.vue';
import GamCounter from '@/views/components/GamCounter.vue';
import GamExpandImage from '@/views/components/GamExpandImage.vue';
import GamImage from '@/views/components/GamImage.vue';
import GamScrollItems from '@/views/components/GamScrollItems.vue';
import GamSome from '@/views/components/GamSome.vue';
import GamSwitch from '@/views/components/GamSwitch.vue';
import { GamImageSize } from '@/views/composables/constants/components/gamImage.constants';
import { GamInputName } from '@/views/composables/constants/components/gamInput.constants';
import { GamListId } from '@/views/composables/constants/components/gamIntersect.constants';
import { GamSwitchPosition, GamSwitchValueType } from '@/views/composables/constants/components/gamSwitch.constants';
import type { GamButtonMessageType } from '@/views/composables/models/components/GamButton';
import type { GamCounterType } from '@/views/composables/models/components/GamCounter';
import type { GamDropdownItem } from '@/views/composables/models/components/GamDropdown';
import type { GamExpandImageType } from '@/views/composables/models/components/GamImage';
import type { GamInputType } from '@/views/composables/models/components/GamInput';
import type { GamLocationFormType } from '@/views/composables/models/components/GamLocation';
import type { GamChessFeatureListType, GamMapFiltersListType } from '@/views/composables/models/components/GamMap';
import type { GamScrollItemsType } from '@/views/composables/models/components/GamScrollItems';
import type { GamSwitchType } from '@/views/composables/models/components/GamSwitch';
import type { LocationFormProps, LocationValidator } from '@/views/composables/models/form.interface';
import { storeToRefs } from 'pinia';
import { computed, nextTick, onBeforeMount, reactive, ref, watch } from 'vue';

import LocationCreateFields from './LocationCreateFields.vue';
import OpenHoursFields from './OpenHoursFields.vue';
import { isLocationValid } from './validateLocation';

const props = defineProps<GamLocationFormType>();

const listId = GamListId.LOCATION_FORM;

const formStore = useFormStore<LocationFormProps, LocationValidator>(props.formId)();
const { gamForm } = storeToRefs(formStore);
const authStore = useAuthStore();
const { userDetails } = storeToRefs(authStore);

const selectedImage = ref<GamExpandImageType | null>();
const imageExpand = ref<InstanceType<typeof GamExpandImage>>();
const isValid = ref<boolean>(false);

const showOpenHours = computed(() => {
  return Array.isArray(gamForm.value?.form.openingHours);
});

const handleToggleOpenHours = (enabled: boolean) => {
  if (!gamForm.value) return;

  if (enabled) {
    gamForm.value.form.openingHours = [];
    for (let d = 1; d <= 5; d++) {
      gamForm.value.form.openingHours.push({ day: d, start: '09:00', end: '17:00' });
    }
    return;
  }

  gamForm.value.form.openingHours = null;
};

const lastSelectedResult = ref<GamDropdownItem>();

/*
const locationVibeOptions = [
  { value: LocationGeneralFeature.ACCOMODATION, label: 'gambit.detail.tag.category.accomodation' },
  { value: LocationGeneralFeature.SCHOOL, label: 'gambit.detail.tag.category.school' },
  { value: LocationGeneralFeature.FOOD, label: 'gambit.detail.tag.category.food' },
  { value: LocationGeneralFeature.DRINKS, label: 'gambit.detail.tag.category.drinks' },
  { value: LocationGeneralFeature.NOISY, label: 'gambit.detail.tag.category.noisy' },
  { value: LocationGeneralFeature.CALM, label: 'gambit.detail.tag.category.calm' },
  { value: LocationGeneralFeature.CASUAL, label: 'gambit.detail.tag.category.casual' },
  { value: LocationGeneralFeature.PROFESSIONAL, label: 'gambit.detail.tag.category.professional' },
  { value: LocationGeneralFeature.TERRACE, label: 'gambit.detail.tag.category.terrace' },
  { value: LocationGeneralFeature.ART, label: 'gambit.detail.tag.category.art' },
  { value: LocationGeneralFeature.CLUBBING, label: 'gambit.detail.tag.category.clubbing' },
  { value: LocationGeneralFeature.LIVE_MUSIC, label: 'gambit.detail.tag.category.live_music' },
  { value: LocationGeneralFeature.VIEW, label: 'gambit.detail.tag.category.view' },
  { value: LocationGeneralFeature.PARK, label: 'gambit.detail.tag.category.park' },
  { value: LocationGeneralFeature.WATER, label: 'gambit.detail.tag.category.water' },
];
*/

/**
 * A location loaded without its schedule must not be mistaken for one that has none. The update
 * payload omits `undefined` but sends `null`, and null clears the stored hours — so only switching
 * opening hours off in the form may produce a null.
 */
const openingHoursFromProps = (location: GamLocationFormType['location']) => {
  if (location && !('openingHours' in location)) {
    return undefined;
  }

  return location?.openingHours?.length ? location.openingHours : null;
};

const formFromProps = (props: GamLocationFormType) => {
  return {
    name: props.location?.name || '',
    address: props.location?.address || '',
    type: props.location?.type ? [props.location.type] : [LocationType.CHESS_FRIENDLY],
    chessFeatures: props.location?.chessFeatures || [],
    generalFeatures: props.location?.generalFeatures || [],
    coordinates: props.location?.coordinates,
    chessSetCount: props.location?.chessSetCount || null,
    chessClockCount: props.location?.chessClockCount || null,
    chessTableCount: props.location?.chessTableCount || null,
    giantChessBoardCount: props.location?.giantChessBoardCount || null,
    giantChessPieces: props.location?.giantChessPieces || false,
    about: props.location?.about || '',
    photos: [],
    isVetted: props.location?.isVetted || false,
    isSparked: props.location?.isSparked || false,
    openingHours: openingHoursFromProps(props.location),
  };
};

function getForm(props: GamLocationFormType) {
  return reactive(formFromProps(props));
}

const reactiveForm = ref();

const expandImage = (hash: string) => {
  selectedImage.value = {
    selectedIndex: 0,
    blobSource: BlobSource.LOCATION,
    images: [
      {
        id: hash,
        imageBlobHash: hash,
        liked: false,
        user: null,
      },
    ],
    canDelete: true,
    closeAfterDelete: true,
    noFilter: true,
  };
  nextTick(() => {
    imageExpand.value?.show();
  });
};

const addImage = (hash: string) => {
  if (!gamForm.value) return;
  if (!Array.isArray(gamForm.value.form.photos)) {
    gamForm.value.form.photos = [];
  }
  gamForm.value.form.photos.push(hash);
};

const clearSelected = () => {
  selectedImage.value = null;
};

const deleteImage = (hash: string) => {
  if (!gamForm.value) return;
  if (!Array.isArray(gamForm.value.form.photos)) {
    gamForm.value.form.photos = [];
    return;
  }
  GamHelper.removeItem(gamForm.value.form.photos, hash);
};

const getSwitch = (): GamSwitchType => {
  return {
    label: 'locations.add.form.section.info.giantChessPieces',
    name: '',
    checked: !!gamForm.value?.form.giantChessPieces,
    type: GamSwitchValueType.SWITCH,
    position: GamSwitchPosition.RIGHT,
  };
};

const getNearbyAddress = (humanizeLabel = true): GamButtonMessageType | undefined => {
  if (lastSelectedResult.value?.nearbyPlaceAddress) {
    let label = lastSelectedResult.value.nearbyPlaceAddress;
    if (humanizeLabel) {
      label = localizeWithValues('locations.add.form.search.nearest.address', [label]);
    }
    return {
      label,
    };
  }
};

const handleUseNearbyLocation = () => {
  if (!gamForm.value) return;
  if (!lastSelectedResult.value?.nearbyPlaceAddress) return;

  gamForm.value.form.address = lastSelectedResult.value.nearbyPlaceAddress;
  gamForm.value.form.name = lastSelectedResult.value.label;
  if (lastSelectedResult.value.coordinates) {
    gamForm.value.form.coordinates = lastSelectedResult.value.coordinates;
  }
  lastSelectedResult.value.nearbyPlaceAddress = undefined;
};

const AUTO_CHOOSE_SUGGESTED_LOCATION = false;

const getNameInput = computed((): GamInputType => {
  return {
    name: GamInputName.GAM_NAME,
    value: gamForm.value?.form.name,
    placeholder: 'locations.add.form.name.placeholder',
  };
});

const isChessSetSelected = computed((): boolean => {
  return !!gamForm.value?.form.chessFeatures.includes(LocationChessFeature.CHESS_SET);
});

const isChessClockSelected = computed((): boolean => {
  return !!gamForm.value?.form.chessFeatures.includes(LocationChessFeature.CHESS_CLOCK);
});

const isChessTableSelected = computed((): boolean => {
  return !!gamForm.value?.form.chessFeatures.includes(LocationChessFeature.CHESS_TABLE);
});

const isGiantChessSelected = computed((): boolean => {
  return !!gamForm.value?.form.chessFeatures.includes(LocationChessFeature.GIANT_CHESS);
});

const getChessSetCounter = computed((): GamCounterType => {
  return {
    label: 'locations.add.form.section.info.numOfChessSets',
    value: gamForm.value?.form.chessSetCount || 0,
    input: {
      min: 0,
    },
  };
});

const getChessClockCounter = computed((): GamCounterType => {
  return {
    label: 'locations.add.form.section.info.numOfChessClocks',
    value: gamForm.value?.form.chessClockCount || 0,
    input: {
      min: 0,
    },
  };
});

const getChessTableCounter = computed((): GamCounterType => {
  return {
    label: 'locations.add.form.section.info.numOfChessTables',
    value: gamForm.value?.form.chessTableCount || 0,
    input: {
      min: 0,
    },
  };
});

const getGiantChessCounter = computed((): GamCounterType => {
  return {
    label: 'locations.add.form.section.info.numOfGiantChess',
    value: gamForm.value?.form.giantChessBoardCount || 0,
    input: {
      min: 0,
    },
  };
});

const getLocTypeList = computed((): GamMapFiltersListType => {
  return {
    listId,
    value: gamForm.value?.form.type,
    isMultiSelect: true,
    isAddMode: true,
  };
});

const getLocFeatureList = computed((): GamChessFeatureListType => {
  return {
    listId,
    value: gamForm.value?.form.chessFeatures,
    isMultiSelect: true,
    isAddMode: true,
  };
});

const getGamScrollItemType = computed((): GamScrollItemsType => {
  return {
    defaultBg: 'var(--color-dark-700)',
    gap: 8,
    spacing: 18,
    preventDrag: true,
  };
});

const setFormValue = (value: Partial<AdminDetailLocationDto>) => {
  const form = {
    ...(formStore.gamForm?.form ?? {}),
    ...value,
  } as LocationFormProps;
  formStore.setForm({ form });
};

// General options

/*
const toggleGeneralFeature = (feature: any) => {
  if (!gamForm.value) {
    return;
  }

  const currentFeatures = gamForm.value.form.generalFeatures || [];

  if (currentFeatures.includes(feature.value)) {
    gamForm.value.form.generalFeatures = currentFeatures.filter((t) => t !== feature.value);
  } else {
    gamForm.value.form.generalFeatures.push(feature.value);
  }
};
*/

// Observer

onBeforeMount(() => {
  reactiveForm.value = getForm(props);
  formStore.setForm({ form: reactiveForm.value });
});

watch(
  () => props.instanceId,
  () => {
    reactiveForm.value = getForm(props);
    formStore.setForm({ form: reactiveForm.value });
  },
);

watch(
  () => formStore.gamForm?.form.pinpoint,
  async (newPoint) => {
    if (!newPoint) return;
    const { lat, lng } = newPoint as unknown as GeoCoords;

    const { coordinates } = formStore.gamForm?.form || {};
    if (newPoint.lat === coordinates?.lat && newPoint.lng === coordinates.lng) return;

    const result = await gambits.locationService.reverseGeocode(lat, lng);
    const loc = (result?.data as any) || {};

    if (!loc || !gamForm.value || !gamForm.value.form.address === loc.address) return;

    gamForm.value.form.address = loc.address;
    gamForm.value.form.name = loc.name;
    gamForm.value.form.coordinates = loc.coordinates;

    // @ts-ignore
    const map = window.gMap.map;

    // @ts-ignore
    if (!map || !newPoint.lat || !newPoint.lng) return;

    // @ts-ignore
    map.panTo(newPoint);
  },
);

watch(isChessSetSelected, (value) => {
  if (!gamForm.value || value) return;
  gamForm.value.form.chessSetCount = undefined;
});

watch(isChessClockSelected, (value) => {
  if (!gamForm.value || value) return;
  gamForm.value.form.chessClockCount = undefined;
});

watch(isChessTableSelected, (value) => {
  if (!gamForm.value || value) return;
  gamForm.value.form.chessTableCount = undefined;
});

watch(isGiantChessSelected, (value) => {
  if (!gamForm.value || value) return;
  gamForm.value.form.giantChessBoardCount = undefined;
});

watch(lastSelectedResult, (item) => {
  if (AUTO_CHOOSE_SUGGESTED_LOCATION && (item as SearchLocationDto)?.accuracy === 'rooftop') {
    handleUseNearbyLocation();
  }
});

watch(
  gamForm,
  (gamForm) => {
    isValid.value = !!isLocationValid(gamForm);
  },
  { deep: true },
);

defineExpose({
  setFormValue,
  getNearbyAddress,
});
</script>

<style lang="scss" scoped>
@use '@/ui/css/partial';

@keyframes breathe {
  0% {
    color: var(--color-white-30);
  }

  100% {
    color: var(--color-white-100);
  }
}

.location-form {
  display: flex;
  position: relative;
  width: 100%;
  flex-direction: column;
  gap: calc(2 * var(--spacing-l));
}

.panel-header {
  display: flex;
  padding: var(--spacing-md) 0;
  align-items: center;
  gap: var(--spacing-md);
  align-self: stretch;

  .header-icon {
    display: flex;
    padding: var(--spacing-xs);
    justify-content: center;
    align-items: center;
  }

  .header-title {
    font-size: 20px;
    font-weight: 700;
  }
}

.location-wrapper {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  gap: var(--spacing-l);
}

.section {
  display: flex;
  flex-direction: column;
  align-self: stretch;
  gap: var(--spacing-l);
}

.section-title {
  padding: 0 0 var(--spacing-l) 0;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.15em;
  position: relative;
  color: var(--color-white-30);
  text-transform: uppercase;
  display: flex;
  gap: 0.25rem;
  align-items: center;
  justify-content: flex-start;

  &:after {
    content: '';
    width: 100%;
    height: 1px;
    background-color: var(--color-white-5);
    display: block;
    position: absolute;
    bottom: 0;
  }

  .required {
    font-weight: 300;
    text-transform: none;
    letter-spacing: 0.1em;
    color: var(--color-white-30);
    transition: color var(--transition);
  }
}

.is-invalid .required {
  animation: breathe 4s ease-in-out alternate infinite;
}

.additional-info {
  padding-top: var(--spacing-l);
  gap: var(--spacing-l);
}

.photos-row-container {
  width: calc(100% + 36px);
  position: relative;
  left: -18px;

  &:after,
  &:before {
    content: '';
    height: 100%;
    width: 18px;
    position: absolute;
    top: 0;
    z-index: 10;
    pointer-events: none;
  }

  &:after {
    background: linear-gradient(to right, rgba(0, 0, 0, 0) 0, var(--color-dark-700) 50%, var(--color-dark-700));
    right: 0;
  }

  &:before {
    background: linear-gradient(to left, rgba(0, 0, 0, 0) 0, var(--color-dark-700) 50%, var(--color-dark-700));
    left: 0;
  }
}

.photos-row {
  display: flex;
  gap: var(--spacing-md);
  padding: 0 18px 10px;
}

.gam-input .gam-input-container.counter {
  padding: var(--spacing-xs) var(--spacing-l);
}

.gam-counter .gam-counter-container .input-count {
  min-width: 100px;
}

.location-types .gam-switch-container {
  height: 50px;
  align-items: center;
}

.general-features {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 0.5rem;

  div {
    text-transform: uppercase;
    cursor: pointer;
  }
}
</style>
